// src/components/UserFiles/UserFilesPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Divider,
} from '@mui/material';

import Sidebar from '../Sidebar/Sidebar';
import useAuth from '../../hooks/useAuth';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { fetchFilesByUser, searchFilesByUser } from '../../services/userFileService';
import { PROCESS_TYPES } from '../../constants/processTypes';

import UserFilesUploadSection from './UserFilesUploadSection';
import UserFilesList from './UserFilesList/UserFilesList';
import UserFilesSearchBar from './UserFilesSearchBar';
import UserFilesHelp from './UserFilesHelp';

// ✅ Replaced boolean flag with processType prop
const UserFilesPage = ({ processType = PROCESS_TYPES.ANONYMIZATION }) => {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [error, setError] = useState(null);

  // pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // search state
  const [searchQuery, setSearchQuery] = useState('');
  const isSearching = !!searchQuery.trim();

  // ---------------------------
  // Load files for current user
  // ---------------------------
  const loadFiles = useCallback(
    async (targetPage = 1, opts = {}) => {
      if (!user) return;

      const effectiveQuery =
        typeof opts.query === 'string' ? opts.query : searchQuery;

      setLoadingFiles(true);
      setError(null);

      try {
        const profileId = user.profile_id;

        let res;
        if (effectiveQuery) {
          // SEARCH MODE: (userId, query, page, pageSize, processType)
          res = await searchFilesByUser(
            profileId,
            effectiveQuery,
            targetPage,
            pageSize,
            processType
          );
        } else {
          // NORMAL LIST MODE: (userId, page, pageSize, processType)
          res = await fetchFilesByUser(
            profileId,
            targetPage,
            pageSize,
            processType
          );
        }

        if (res.success) {
          const payload = res.data || {};
          const items = payload.items || [];

          setFiles(items);
          setTotalCount(
            typeof payload.count === 'number' ? payload.count : items.length
          );
          setPage(targetPage);
        } else {
          const backendErr = res.errors || {};
          const msg =
            backendErr.detail ||
            t('files.load_failed', 'Nepodarilo sa načítať súbory.');
          setError(msg);
          enqueueSnackbar(msg, { variant: 'error' });
        }
      } catch (e) {
        console.error('Error loading files', e);
        const msg = t('files.load_failed', 'Nepodarilo sa načítať súbory.');
        setError(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      } finally {
        setLoadingFiles(false);
      }
    },
    [user, pageSize, searchQuery, processType, enqueueSnackbar, t]
  );

  useEffect(() => {
    if (isLoggedIn && user) {
      // always start on page 1 when user arrives / changes / pageSize or searchQuery changes
      loadFiles(1);
    }
  }, [isLoggedIn, user, loadFiles]);

  const handlePageChange = (newPage) => {
    loadFiles(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    // loadFiles(1) is triggered indirectly via effect because loadFiles changes (pageSize in deps)
  };

  const handleSearch = (query) => {
    const trimmed = query.trim();
    setSearchQuery(trimmed);
    // Explicitly pass query so loadFiles uses fresh value immediately
    loadFiles(1, { query: trimmed });
  };

  if (!isLoggedIn || !user) {
    return (
      <>
        <Sidebar user={user} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Sidebar user={user} />

      <Container maxWidth="xl" sx={{ mb: 6 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, md: 4 }, 
            mt: 4, 
            borderRadius: 3, 
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            // Use native MUI shadow instead of hardcoded rgba so it handles dark mode cleanly
            boxShadow: 2, 
          }}
        >
          <Grid container spacing={4}>
            {/* Header */}
            <Grid item xs={12}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                {t('files.title', 'Moje súbory')}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
                {t(
                  'files.subtitle',
                  'Nahrávajte, sťahujte a podpisujte svoje súbory uložené v systéme.'
                )}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <UserFilesHelp t={t} />
            </Grid>

            {/* Top-level load error */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
              </Grid>
            )}

            {/* Action Row */}
            <Grid item xs={12} md={6}>
              <UserFilesSearchBar
                t={t}
                loading={loadingFiles}
                initialQuery={searchQuery}
                onSearch={handleSearch}
              />
            </Grid>

            {/* Upload section - handles its own upload logic, calls loadFiles on success */}
            <Grid item xs={12} md={6}>
              <UserFilesUploadSection
                t={t}
                user={user}
                onFilesChanged={() => loadFiles(page)}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Divider natively uses theme divider color when borderColor isn't overridden */}
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Files list - handles sign/delete/download logic */}
            <Grid item xs={12}>
              <UserFilesList
                user={user}
                t={t}
                files={files}
                loadingFiles={loadingFiles}
                onFilesChanged={() => loadFiles(page)}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                isSearching={isSearching}
                searchQuery={searchQuery}
              />
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </>
  );
};

export default UserFilesPage;